import dayjs from 'dayjs';

import { useNotificationQueryActions } from '@/api/hooks/notificationQueryActions';
import {
  type NotificationResponse,
  type NotificationResponsePaginatedResponse,
  NotificationType,
  ReviewOutcome,
} from '@/api/schema/data-contracts';
import { useMessages } from '@/hooks/messages';
import type { Notification } from '@/types';

import { NotificationMessages } from './messages';

export const useNotifications = () => {
  const messages = useMessages(NotificationMessages);

  const { useGetNotifications, useReadNotification } = useNotificationQueryActions();
  const {
    data: notificationsResponse,
    isLoading,
    refetch,
  } = useGetNotifications({
    sortAscending: false,
    pageSize: 50,
  }) as {
    data: NotificationResponsePaginatedResponse | undefined;
    isLoading: boolean;
    refetch: () => void;
  };
  const { mutate: readNotification } = useReadNotification();

  const getNotificationMessage = (notification: NotificationResponse) => {
    // EWM-1143: the estimation this notification points at may already be deleted, in which case
    // the backend has no project to name and sends an empty string. Interpolating that straight
    // into "estimation ({projectName})" rendered a bare "()" instead of a readable marker.
    const projectName = notification.projectName || messages.deletedProject();

    if (notification.notificationType === NotificationType.ESTIMATION_REVIEW_OUTCOME) {
      if (notification.reviewOutcome === ReviewOutcome.COMPLETED) {
        return messages.estimationReviewOutcomeCompleted({ projectName });
      }
      if (notification.reviewOutcome === ReviewOutcome.CANCELED) {
        return messages.estimationReviewOutcomeCanceled({ projectName });
      }
      return messages.estimationReviewOutcomeCompleted({ projectName });
    }

    const messageByNotificationType: Record<
      Exclude<NotificationType, NotificationType.ESTIMATION_REVIEW_OUTCOME>,
      string
    > = {
      USER_ASSIGNED_AS_CONTRIBUTOR_TO_ESTIMATION: messages.userAssignedAsContributorToEstimation({
        projectName,
      }),
      USER_ASSIGNED_AS_PLANNING_RESPONSIBLE_TO_ESTIMATION:
        messages.userAssignedAsPlanningResponsibleToEstimation({
          projectName,
        }),
      USER_ASSIGNED_AS_REVIEWER_TO_ESTIMATION: messages.userAssignedAsReviewerToEstimation({
        projectName,
      }),
      USER_ASSIGNED_AS_PHASE_LEAD_TO_ESTIMATION_ITEM: messages.userAssignedAsPhaseLead({
        projectName,
      }),
      ESTIMATION_REVIEW_REQUESTED: messages.reviewRequested({
        projectName,
      }),
      ESTIMATION_APPROVED: messages.reviewApproved({ projectName }),
      ESTIMATION_REVIEW_CANCELLED: messages.reviewCancelled({
        projectName,
      }),
      COMMENT_ADDED_TO_ESTIMATION_ITEM: messages.commentAddedToEstimationItem({
        projectName,
      }),
      ESTIMATION_NEW_VERSION_CREATED: messages.estimationNewVersionCreated({
        projectName,
      }),
      ESTIMATION_AI_REVIEW_CREATED: messages.estimationAiReviewCreated({
        projectName,
      }),
      REVIEW_COMMENTS_SUMMARY_AVAILABLE: messages.reviewCommentsSummaryAvailable({
        projectName,
      }),
      PHASE_STATUS_CHANGED: messages.phaseStatusChanged({
        projectName,
      }),
      PHASE_BLOCKED: messages.phaseBlocked({ projectName }),
      PHASE_UNBLOCKED: messages.phaseUnblocked({ projectName }),
      ITEM_ASSIGNED_TO_PHASE: messages.itemAssignedToPhase({
        projectName,
      }),
      PHASE_DEADLINE_APPROACHING: messages.phaseDeadlineApproaching({
        projectName,
      }),
      CONVERSATION_SHARED: messages.conversationShared(),
    };

    return messageByNotificationType[
      notification.notificationType as Exclude<
        NotificationType,
        NotificationType.ESTIMATION_REVIEW_OUTCOME
      >
    ];
  };

  const getNotifications = (): {
    data: Notification[];
    totalCount: number;
    isLoading?: boolean;
  } => {
    if (isLoading) {
      return { data: [], totalCount: 0, isLoading: true };
    }
    if (!notificationsResponse) {
      return { data: [], totalCount: 0 };
    }

    const { items: notifications, totalCount } = notificationsResponse;
    return {
      data: notifications.map((notification) => ({
        id: notification.id,
        type: notification.notificationType,
        message: getNotificationMessage(notification),
        timeCreated: dayjs(notification.createdAt),
        isRead: notification.isRead,
        estimationId: notification.estimationId,
        conversationId: notification.conversationId,
      })),
      totalCount,
    };
  };

  return { getNotifications, readNotification, refetchNotifications: refetch };
};
